import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { isAdmin, isMaster, hasRight, canViewWebCheckins, canAssignRights, canUseQuotes, canManageUjhi, canAcquisition, canViewAgents, canChangeAgentRates } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { WelcomeSplash } from "@/components/WelcomeSplash";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Analytics } from "@/lib/analytics";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const OverviewPage = lazy(() => import("@/pages/OverviewPage"));
const PropertiesPage = lazy(() => import("@/pages/PropertiesPage"));
const OccupancyPage = lazy(() => import("@/pages/OccupancyPage"));
const DayOccupancyPage = lazy(() => import("@/pages/DayOccupancyPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const PromotionsPage = lazy(() => import("@/pages/PromotionsPage"));
const RatesPage = lazy(() => import("@/pages/RatesPage"));
const StorePage = lazy(() => import("@/pages/StorePage"));
const BookingsPage = lazy(() => import("@/pages/BookingsPage"));
const QuotesPage = lazy(() => import("@/pages/QuotesPage"));
const AgentsPage = lazy(() => import("@/pages/AgentsPage"));
const AgentNamesPage = lazy(() => import("@/pages/AgentNamesPage"));
const StaffPage = lazy(() => import("@/pages/StaffPage"));
const OperationsPage = lazy(() => import("@/pages/OperationsPage"));
const WebCheckinsPage = lazy(() => import("@/pages/WebCheckinsPage"));
const UjhiPage = lazy(() => import("@/pages/UjhiPage"));
const AcquisitionPage = lazy(() => import("@/pages/AcquisitionPage"));
const AgentOnboardingPage = lazy(() => import("@/pages/AgentOnboardingPage"));
const WebCheckinPublic = lazy(() => import("@/pages/WebCheckinPublic"));

const PageLoader = () => (
  <div className="flex h-[60vh] items-center justify-center" data-testid="page-loader">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-[#E63946]" />
  </div>
);

const Protected = ({ children, guard }) => {
  const { auth } = useAuth();
  if (!auth) return <Navigate to="/login" replace />;
  if (guard && !guard(auth)) return <Navigate to="/" replace />;
  return children;
};

const LoginGate = () => {
  const { auth } = useAuth();
  if (auth) return <Navigate to="/" replace />;
  return <LoginPage />;
};

const IndexHome = () => {
  const { auth } = useAuth();
  if (auth?.kind === "agent") return <Navigate to="/properties" replace />;
  return <OverviewPage />;
};

function App() {
  return (
    <AuthProvider>
      <WelcomeSplash />
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Analytics />
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginGate />} />
              <Route path="/checkin/:propertyId" element={<WebCheckinPublic />} />
              <Route
                path="/"
                element={
                  <Protected>
                    <AppLayout />
                  </Protected>
                }
              >
                <Route index element={<IndexHome />} />
                <Route path="properties" element={<PropertiesPage />} />
                <Route path="occupancy" element={<OccupancyPage />} />
                <Route path="day-occupancy" element={<Protected guard={isAdmin}><DayOccupancyPage /></Protected>} />
                <Route path="bookings" element={<BookingsPage />} />
                <Route path="quotes" element={<Protected guard={canUseQuotes}><QuotesPage /></Protected>} />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="promotions" element={<PromotionsPage />} />
                <Route path="rates" element={<RatesPage />} />
                <Route path="store" element={<Protected guard={(a) => isMaster(a) || hasRight(a, "store.view")}><StorePage /></Protected>} />
                <Route path="agents" element={<Protected guard={(a) => isMaster(a) || canChangeAgentRates(a)}><AgentsPage /></Protected>} />
                <Route path="agent-names" element={<Protected guard={canViewAgents}><AgentNamesPage /></Protected>} />
                <Route path="staff" element={<Protected guard={canAssignRights}><StaffPage /></Protected>} />
                <Route path="operations" element={<Protected guard={isAdmin}><OperationsPage /></Protected>} />
                <Route path="web-checkins" element={<Protected guard={canViewWebCheckins}><WebCheckinsPage /></Protected>} />
                <Route path="ujhi" element={<Protected guard={canManageUjhi}><UjhiPage /></Protected>} />
                <Route path="acquisition" element={<Protected guard={canAcquisition}><AcquisitionPage /></Protected>} />
                <Route path="agent-onboarding" element={<Protected guard={isAdmin}><AgentOnboardingPage /></Protected>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
